import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useField } from "formik";
import { ChevronDown } from "lucide-react";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/checkmark.svg";

interface Option {
  value: string;
  label: string;
}

interface GradeRangeDropdownProps {
  label?: string;
  required?: boolean;
  options: Option[];
  name: string;
  placeHolder?: string;
  labelClassNames?: string;
  controlCustomStyle?: any;
  placeholderCustomStyle?: any;
  disable?: boolean;
  isCreatable?: boolean;
  customHeight?: string;
}

const GradeRangeDropdown: React.FC<GradeRangeDropdownProps> = ({
  label,
  options = [],
  name,
  placeHolder = "Pick grades",
  required,
  labelClassNames,
  controlCustomStyle,
  disable,
}) => {
  const [field, meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const [fromGrade, setFromGrade] = useState("");
  const [toGrade, setToGrade] = useState("");
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const isError = meta.touched && !!meta.error;
  const selectedValues: string[] = field.value || [];

  const computePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, []);

  const handleOpen = () => {
    if (disable) return;
    computePosition();
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPanelStyle({
          position: "fixed",
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 9999,
        });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !panelRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const handleSelectRange = () => {
    if (!fromGrade || !toGrade) return;
    const fromIdx = options.findIndex((o) => o.value === fromGrade);
    const toIdx = options.findIndex((o) => o.value === toGrade);
    if (fromIdx === -1 || toIdx === -1) return;
    const start = Math.min(fromIdx, toIdx);
    const end = Math.max(fromIdx, toIdx);
    const rangeValues = options.slice(start, end + 1).map((o) => o.value);
    const merged = Array.from(new Set([...selectedValues, ...rangeValues]));
    helpers.setValue(merged, false);
  };

  const toggleGrade = (value: string) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    helpers.setValue(next, false);
  };

  const isPlaceholder = selectedValues.length === 0;

  return (
    <div className="flex flex-col w-full gap-[0.198vw] relative pb-[2.2vh]">
      {!!label && (
        <label
          className={`font-inter text-[1.75vh] text-[#374151] font-[500] mb-[0.4vh] pl-[0.63vw] tracking-[0.01em] ${labelClassNames || ""}`}
        >
          {label} {!!required && <span style={{ color: "#E24B4A" }}>*</span>}
        </label>
      )}

      <div
        ref={triggerRef}
        onClick={handleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "4px",
          borderRadius: "5.4px",
          minHeight: "4.4vh",
          boxSizing: "border-box",
          padding: "4px 8px 4px 7.2px",
          border: isError ? "1px solid red" : "1px solid #9d9d9d",
          outline: isOpen ? "2px solid #9B97F0" : "none",
          outlineOffset: isOpen ? "1px" : "auto",
          cursor: disable ? "not-allowed" : "pointer",
          background: "#fff",
          opacity: disable ? 0.6 : 1,
          userSelect: "none",
          ...(controlCustomStyle || {}),
        }}
      >
        {isPlaceholder ? (
          <span
            style={{
              fontSize: "1.766vh",
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              color: "#9CA3AF",
              flex: 1,
            }}
          >
            {placeHolder}
          </span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", flex: 1 }}>
            {selectedValues.map((val) => {
              const opt = options.find((o) => o.value === val);
              return (
                <div
                  key={val}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "5.4px",
                    height: "2.7vh",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "0 2px 0 6px",
                      fontSize: "1.62vh",
                      fontFamily: "Inter, sans-serif",
                      color: "#333",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opt?.label || val}
                  </div>
                  <div
                    onClick={(e) => { e.stopPropagation(); toggleGrade(val); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 4px",
                      borderRadius: "0 5.4px 5.4px 0",
                      cursor: "pointer",
                      color: "#cccccc",
                      transition: "background-color 0.15s ease, color 0.15s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FFBDAD"; e.currentTarget.style.color = "#DE350B"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#cccccc"; }}
                  >
                    <CloseIcon height="14" width="14" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isPlaceholder && (
          <>
            <div
              onClick={(e) => { e.stopPropagation(); helpers.setValue([], false); }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 6px", cursor: "pointer", color: "#cccccc",
                transition: "color 0.15s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#DE350B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#cccccc"; }}
            >
              <CloseIcon height="20" width="20" />
            </div>
            <div style={{ width: 1, height: "60%", backgroundColor: "#cccccc", flexShrink: 0 }} />
          </>
        )}
        <ChevronDownIcon
          width="9"
          height="5.4"
          style={{
            flexShrink: 0,
            marginLeft: "6px",
            marginRight: "2px",
            color: "#cccccc",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </div>

      {/* Error */}
      <div className="absolute bottom-0 left-[0.4vw] right-0 flex flex-col justify-start overflow-hidden">
        {isError && (
          <div className="text-[#E24B4A] text-[1.6vh] leading-none">
            {Array.isArray(meta.error) ? meta.error.join(", ") : meta.error}
          </div>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              ...panelStyle,
              background: "rgba(255,255,255,0.96)",
              border: "1.8px solid rgba(0,0,0,0.1)",
              borderRadius: "7.2px",
              backdropFilter: "blur(18px)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              overflow: "hidden",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 12px",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                background: "#FAFAFA",
              }}
            >
              <span style={{ fontSize: "1.5vh", color: "#6B7280", fontWeight: 500, fontFamily: "Inter, sans-serif", flexShrink: 0 }}>From</span>
              <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                <div
                  onClick={(e) => { e.stopPropagation(); setFromOpen(!fromOpen); setToOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontSize: "1.5vh", fontFamily: "Inter, sans-serif", fontWeight: 400,
                    border: fromOpen ? "1px solid #9B97F0" : "1px solid #D1D5DB", borderRadius: 6,
                    padding: "4px 6px", background: "#fff", cursor: "pointer",
                    color: fromGrade ? "#111827" : "#9CA3AF",
                    boxShadow: fromOpen ? "0 0 0 2px rgba(155,151,240,0.25)" : "none",
                    transition: "all 0.12s ease",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fromGrade ? options.find(o => o.value === fromGrade)?.label : "Select"}</span>
                  <ChevronDown size={12} style={{ flexShrink: 0, marginLeft: 2, color: "#9CA3AF", transform: fromOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                </div>
                {fromOpen && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 10,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)", maxHeight: 160, overflowY: "auto",
                    fontFamily: "Inter, sans-serif",
                  }}>
                    {options.map((o) => (
                      <div key={o.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFromGrade(o.value);
                          if (toGrade) {
                            const newIdx = options.findIndex(x => x.value === o.value);
                            const toIdx = options.findIndex(x => x.value === toGrade);
                            if (newIdx > toIdx) setToGrade("");
                          }
                          setFromOpen(false);
                        }}
                        style={{
                          padding: "6px 10px", fontSize: "1.5vh", fontWeight: fromGrade === o.value ? 600 : 400,
                          color: fromGrade === o.value ? "#fff" : "#374151",
                          background: fromGrade === o.value ? "#374AF2" : "transparent",
                          cursor: "pointer", transition: "all 0.1s ease",
                        }}
                        onMouseEnter={(e) => { if (fromGrade !== o.value) e.currentTarget.style.background = "#F3F4F6"; }}
                        onMouseLeave={(e) => { if (fromGrade !== o.value) e.currentTarget.style.background = "transparent"; }}
                      >
                        {o.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <span style={{ fontSize: "1.5vh", color: "#6B7280", fontWeight: 500, fontFamily: "Inter, sans-serif", flexShrink: 0 }}>to</span>
              <div style={{ position: "relative", flex: 1, minWidth: 0, opacity: !fromGrade ? 0.45 : 1 }}>
                <div
                  onClick={(e) => { e.stopPropagation(); if (fromGrade) { setToOpen(!toOpen); setFromOpen(false); } }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontSize: "1.5vh", fontFamily: "Inter, sans-serif", fontWeight: 400,
                    border: toOpen ? "1px solid #9B97F0" : "1px solid #D1D5DB", borderRadius: 6,
                    padding: "4px 6px", background: "#fff",
                    cursor: !fromGrade ? "not-allowed" : "pointer",
                    color: toGrade ? "#111827" : "#9CA3AF",
                    boxShadow: toOpen ? "0 0 0 2px rgba(155,151,240,0.25)" : "none",
                    transition: "all 0.12s ease",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{toGrade ? options.find(o => o.value === toGrade)?.label : "Select"}</span>
                  <ChevronDown size={12} style={{ flexShrink: 0, marginLeft: 2, color: "#9CA3AF", transform: toOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                </div>
                {toOpen && fromGrade && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 10,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)", maxHeight: 160, overflowY: "auto",
                    fontFamily: "Inter, sans-serif",
                  }}>
                    {options
                      .slice(options.findIndex((o) => o.value === fromGrade))
                      .map((o) => (
                      <div key={o.value}
                        onClick={(e) => { e.stopPropagation(); setToGrade(o.value); setToOpen(false); }}
                        style={{
                          padding: "6px 10px", fontSize: "1.5vh", fontWeight: toGrade === o.value ? 600 : 400,
                          color: toGrade === o.value ? "#fff" : "#374151",
                          background: toGrade === o.value ? "#374AF2" : "transparent",
                          cursor: "pointer", transition: "all 0.1s ease",
                        }}
                        onMouseEnter={(e) => { if (toGrade !== o.value) e.currentTarget.style.background = "#F3F4F6"; }}
                        onMouseLeave={(e) => { if (toGrade !== o.value) e.currentTarget.style.background = "transparent"; }}
                      >
                        {o.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                disabled={!fromGrade || !toGrade}
                onClick={handleSelectRange}
                style={{
                  flexShrink: 0,
                  fontSize: "1.4vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  padding: "4px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background: !fromGrade || !toGrade ? "#E5E7EB" : "#374AF2",
                  color: !fromGrade || !toGrade ? "#9CA3AF" : "#fff",
                  cursor: !fromGrade || !toGrade ? "not-allowed" : "pointer",
                  transition: "all 0.18s ease",
                  whiteSpace: "nowrap",
                }}
              >
                Apply
              </button>
            </div>

            <div style={{ maxHeight: "220px", overflowY: "auto", padding: "4px 0" }}>
              {options.map((o) => {
                const checked = selectedValues.includes(o.value);
                return (
                  <label
                    key={o.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "6px 14px",
                      cursor: "pointer",
                      background: checked ? "rgba(0,123,255,0.06)" : "transparent",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!checked) (e.currentTarget as HTMLElement).style.background = "rgba(0,123,255,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = checked ? "rgba(0,123,255,0.06)" : "transparent";
                    }}
                  >
                    <div
                      onClick={() => toggleGrade(o.value)}
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "4px",
                        border: checked ? "2px solid rgba(0,123,255,0.85)" : "2px solid #9d9d9d",
                        background: checked ? "rgba(0,123,255,0.85)" : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s ease",
                      }}
                    >
                      {checked && (
                        <CheckmarkIcon width="9" height="7" style={{ color: "#fff" }} />
                      )}
                    </div>
                    <span
                      onClick={() => toggleGrade(o.value)}
                      style={{
                        fontSize: "1.766vh",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 300,
                        color: "#333",
                        flex: 1,
                      }}
                    >
                      {o.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default GradeRangeDropdown;
